import {
    FETCH_BOOKING_LOCATION,
    FETCH_BOOKING_LOCATION_SUCCESS,
    FETCH_BOOKING_LOCATION_FAILED,
    STOP_LOCATION_FETCH,
    STORE_ADRESSES
} from "../store/types";
import store from '../store/store';
import { firebase } from '../config/configureFirebase';
import { query, onValue, set, off, push, limitToLast, serverTimestamp } from "firebase/database";

export const saveTracking = (bookingId, location) => {
    const {
        trackingRef
    } = firebase;
    push(trackingRef(bookingId), location);
}

// The booking id whose /tracking node currently holds our onValue listener.
// Every bookings delivery (driver: bookinglistactions; customer: BookedCabScreen
// on each status echo) calls fetchBookingLocations again, and each call used
// to stack ANOTHER listener on the same node — N listeners, N dispatches per
// tracking write. One listener at a time: same id → no-op; new id → detach
// the previous one first.
let trackedListenerId = null;

export const fetchBookingLocations = (bookingId) => (dispatch) => {

    const {
        trackingRef
    } = firebase;

    if (trackedListenerId === bookingId) return; // already listening
    if (trackedListenerId) {
        try { off(trackingRef(trackedListenerId)); } catch (_) { /* nothing attached */ }
    }
    trackedListenerId = bookingId;

    dispatch({
        type: FETCH_BOOKING_LOCATION,
        payload: bookingId,
    });
    onValue(query(trackingRef(bookingId), limitToLast(1)),(snapshot) => {
        if (snapshot.val()) {
            let data = snapshot.val();
            const locations = Object.keys(data)
                .map((i) => {
                    return data[i]
                });
            if (locations.length == 1) {
                dispatch({
                    type: FETCH_BOOKING_LOCATION_SUCCESS,
                    payload: locations[0]
                });
            }
            else {
                dispatch({
                    type: FETCH_BOOKING_LOCATION_FAILED,
                    payload: store.getState().languagedata.defaultLanguage.location_fetch_error,
                });
            }
        } else {
            dispatch({
                type: FETCH_BOOKING_LOCATION_FAILED,
                payload: store.getState().languagedata.defaultLanguage.location_fetch_error,
            });
        }
    });
};

// The driver's own live position, /locations/{driverUid}: written by every
// driver app (on a trip every 5 s if the car moves, otherwise every 30 s),
// about 100 bytes, with a server-stamped updateTime. The customer's trip screen
// listens here for the whole trip (owner, 1 Nov): the car on the map and the
// minutes to the pickup. /tracking/{bookingId} is the fare's record, written
// only under its own gates (a street hail's first trip has no point at all).
// One listener at a time, off while the customer's app is in the background: an
// estimated 60 KB at most on a 25-minute trip, less than /tracking cost when it was
// the feed (owner: keep the RTDB cost minimal). The reducer's FAILED case never
// clears the coords.
let driverListenerUid = null;

const detachDriverListener = () => {
    if (!driverListenerUid) return;
    const { userLocationRef } = firebase;
    try { off(userLocationRef(driverListenerUid)); } catch (_) { /* nothing attached */ }
    driverListenerUid = null;
};

export const fetchDriverLocation = (driverUid) => (dispatch) => {
    if (!driverUid || driverListenerUid === driverUid) return; // already listening
    detachDriverListener();
    const { userLocationRef } = firebase;
    driverListenerUid = driverUid;
    onValue(userLocationRef(driverUid), (snapshot) => {
        const v = snapshot.val();
        if (!v || typeof v.lat !== 'number' || typeof v.lng !== 'number') return;
        dispatch({
            type: FETCH_BOOKING_LOCATION_SUCCESS,
            payload: { lat: v.lat, lng: v.lng, heading: v.heading, at: v.updateTime },
        });
    }, () => {
        // Refused (rules) or cancelled: forget it so a later call can retry.
        if (driverListenerUid === driverUid) driverListenerUid = null;
    });
};

export const stopDriverLocation = () => () => {
    detachDriverListener();
};

// A nearby car's live position for the customer's map (mobile-app GlidingCars): one
// small node, for as long as the caller keeps it. Returns the way to stop.
export const watchDriverLocation = (driverUid, onFix) => {
    const { userLocationRef } = firebase;
    return onValue(userLocationRef(driverUid), (snapshot) => {
        const v = snapshot.val();
        if (!v || typeof v.lat !== 'number' || typeof v.lng !== 'number') return;
        onFix({ lat: v.lat, lng: v.lng, at: v.updateTime });
    }, () => { /* refused or cancelled: the car stays where the list put it */ });
};

// The driver's own trip ended: detach the /tracking listener of the trip that was
// tracked (bookinglistactions calls this when a delivery has no tracked trip).
// Only that listener: the customer's live-driver listener is a different thing.
export const stopTrackedBookingLocation = () => (dispatch) => {
    const { trackingRef } = firebase;
    if (!trackedListenerId) return;
    const id = trackedListenerId;
    trackedListenerId = null;
    off(trackingRef(id));
    dispatch({ type: STOP_LOCATION_FETCH, payload: id });
};

export const stopLocationFetch = (bookingId) => (dispatch) => {

    const {
        trackingRef
    } = firebase;

    dispatch({
        type: STOP_LOCATION_FETCH,
        payload: bookingId,
    });
    // Forget the dedupe stamp BEFORE detaching so the next
    // fetchBookingLocations re-attaches instead of short-circuiting —
    // but only when THIS booking is the one being listened to. A late
    // stop for an older booking must not forget the live listener's id,
    // or the next fetch would stack a second listener on that node.
    if (trackedListenerId === bookingId) trackedListenerId = null;
    off(trackingRef(bookingId));
    detachDriverListener();
}

export const saveUserLocation = (location) => {
    const {
      auth,
      userLocationRef,
    } = firebase;
    // Guard: auth.currentUser can be null briefly on cold start (Firebase Auth
    // restores the session asynchronously). With cache-first startup the
    // (possibly driver) profile is already in Redux before that, so the GPS /
    // heartbeat effects can call this before currentUser exists — reading
    // `.uid` on null crashed the app. Skip until auth is ready; the next
    // location tick retries.
    const uid = auth.currentUser && auth.currentUser.uid;
    if (!uid) return;
    // Stamp with the SERVER clock (not the device clock). Driver-location
    // freshness is enforced server-side (dispatcher, rebuildActiveDriversSlim,
    // bookingScheduler); using serverTimestamp() makes those comparisons immune
    // to driver/customer device clock skew.
    set(userLocationRef(uid), { ...location, updateTime: serverTimestamp() });
}

export const storeAddresses = (data) => (dispatch) => {
    dispatch({
        type: STORE_ADRESSES,
        payload: data,
    });
}